import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Circle, Award, PlayCircle, Lock, RotateCcw, FileText, HelpCircle, XCircle, Clock } from "lucide-react";
import { VideoPlayer } from "@/components/VideoPlayer";

const lengthLabel = (sec, fallbackMin) => {
  if (Number.isFinite(sec) && sec > 0) { const m = Math.floor(sec / 60); const s = Math.round(sec % 60); return `${m} min ${String(s).padStart(2, "0")} sec`; }
  return fallbackMin ? `${fallbackMin} min` : "";
};

const LessonIcon = ({ done, locked, current }) => {
  if (done) return <CheckCircle2 className="h-4 w-4 text-rag-green flex-shrink-0" />;
  if (locked) return <Lock className="h-4 w-4 text-muted-foreground flex-shrink-0" />;
  if (current) return <PlayCircle className="h-4 w-4 text-brand flex-shrink-0" />;
  return <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0" />;
};
const QUIZ_UNLOCK_PCT = 50;

const QuizMark = ({ ok, index }) => ok
  ? <CheckCircle2 data-testid={`quiz-mark-${index}-correct`} className="h-5 w-5 text-rag-green shrink-0 mt-0.5" />
  : <XCircle data-testid={`quiz-mark-${index}-wrong`} className="h-5 w-5 text-rag-red shrink-0 mt-0.5" />;

const QuizResultCard = ({ result }) => {
  const wrong = result.total - result.correct;
  return (
    <div data-testid="quiz-result" className={`rounded-2xl p-5 border ${result.passed ? "bg-rag-green/10 border-rag-green/30" : "bg-rag-red/10 border-rag-red/30"}`}>
      <div className="flex items-center gap-4">
        <div data-testid="quiz-score" className={`text-5xl font-heading font-bold ${result.passed ? "text-rag-green" : "text-rag-red"}`}>{result.score}%</div>
        <div>
          <div className={`font-heading font-bold text-lg ${result.passed ? "text-rag-green" : "text-rag-red"}`}>{result.passed ? "Passed" : "Not passed yet"}</div>
          <div className="text-sm text-foreground/80" data-testid="quiz-breakdown">
            <span className="font-semibold">{result.correct} of {result.total}</span> correct · <span className="font-semibold">{wrong}</span> {wrong === 1 ? "question" : "questions"} wrong
          </div>
          <div className="text-xs text-muted-foreground mt-1">{result.passed ? "Well done — your certificate progress has been updated." : "You need 50% to pass. Questions marked with a red cross were answered incorrectly — review them and retake."}</div>
        </div>
      </div>
    </div>
  );
};

const lessonClass = (current, locked) => {
  if (current) return "bg-brand-light text-brand font-medium";
  if (locked) return "opacity-50 cursor-not-allowed text-muted-foreground";
  return "hover:bg-cream text-foreground/80";
};


export default function CoursePlayer() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [active, setActive] = useState(location.state?.start || 0);
  const [showTranscript, setShowTranscript] = useState(false);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [celebrate, setCelebrate] = useState(false);
  const [reflection, setReflection] = useState("");
  const [videoLen, setVideoLen] = useState({});
  const [watched, setWatched] = useState(() => { try { return JSON.parse(localStorage.getItem(`kohc_watched_${user.id}_${id}`) || "{}"); } catch { return {}; } });
  const isStaff = user.role === "staff";
  const recordProgress = (lessonId, frac) => setWatched((w) => {
    if ((w[lessonId] || 0) >= frac) return w;
    const next = { ...w, [lessonId]: Math.min(1, frac) };
    localStorage.setItem(`kohc_watched_${user.id}_${id}`, JSON.stringify(next));
    return next;
  });

  const load = useCallback(async () => {
    const { data } = await api.get(`/courses/${id}`);
    setCourse(data);
    if (isStaff) {
      const enr = await api.get("/enrollments/me");
      setEnrollment(enr.data.find((e) => e.course_id === id) || null);
    }
  }, [id, isStaff]);
  useEffect(() => { load(); }, [load]);

  const lessons = course?.lessons || [];
  const lesson = lessons[active];
  const completed = new Set(enrollment?.completed_lessons || []);

  useEffect(() => {
    setShowTranscript(false);
    setAnswers({});
    setResult(null);
  }, [active, lesson?.id]);

  useEffect(() => {
    setReflection(enrollment?.reflections?.[lesson?.id]?.text || "");
  }, [lesson?.id, enrollment]);

  if (!course) return <div className="grid place-items-center py-24 text-muted-foreground">Loading…</div>;

  const isLocked = (i) => isStaff && i > 0 && !completed.has(lessons[i - 1]?.id);
  const currentLocked = isLocked(active);
  const quiz = lesson?.quiz || [];
  const hasQuiz = quiz.length > 0;
  const lessonDone = completed.has(lesson?.id);
  const showStaffQuiz = isStaff && (!lessonDone || !!result);
  const watchedPct = Math.round((watched[lesson?.id] || 0) * 100);
  const quizLocked = isStaff && hasQuiz && !lessonDone && !!lesson?.video_url && watchedPct < QUIZ_UNLOCK_PCT;

  const markComplete = async () => {
    if (!enrollment) { toast.info("This course isn't assigned to you."); return; }
    try {
      const { data } = await api.post(`/enrollments/${enrollment.id}/complete-lesson`, { lesson_id: lesson.id });
      setEnrollment((e) => ({ ...e, progress: data.progress, status: data.status, completed_lessons: [...(e.completed_lessons || []), lesson.id] }));
      if (data.certificate) setCelebrate(true);
      else toast.success("Module marked complete");
      if (active < lessons.length - 1) setActive(active + 1);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const submitQuiz = async () => {
    if (!enrollment) { toast.info("This course isn't assigned to you."); return; }
    if (Object.keys(answers).length < quiz.length) { toast.error("Please answer every question."); return; }
    try {
      const payload = quiz.map((_, i) => (answers[i] ?? -1));
      const { data } = await api.post(`/enrollments/${enrollment.id}/submit-quiz`, { lesson_id: lesson.id, answers: payload });
      setResult(data);
      if (data.passed) {
        setEnrollment((e) => ({ ...e, progress: data.progress, status: data.status, completed_lessons: [...new Set([...(e.completed_lessons || []), lesson.id])] }));
        if (data.certificate) setCelebrate(true);
        else toast.success(`Passed with ${data.score}% 🎉`);
      } else {
        toast.error(`You scored ${data.score}%. You need 50% to pass — review and try again.`);
      }
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const retake = () => { setAnswers({}); setResult(null); };

  const saveReflection = async () => {
    if (!enrollment) { toast.info("This course isn't assigned to you."); return; }
    try {
      await api.post(`/enrollments/${enrollment.id}/reflection`, { lesson_id: lesson.id, text: reflection });
      toast.success("Reflection saved");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  return (
    <div>
      <Dialog open={celebrate} onOpenChange={setCelebrate}>
        <DialogContent data-testid="course-complete-dialog" className="max-w-md text-center">
          <div className="mx-auto h-20 w-20 rounded-full bg-rag-green/10 grid place-items-center"><Award className="h-10 w-10 text-rag-green" /></div>
          <h2 className="font-heading font-bold text-2xl text-brand mt-2">Congratulations!</h2>
          <p className="text-foreground/80">You have completed the <span className="font-semibold text-brand">{course?.title}</span> course.</p>
          <p className="text-sm text-muted-foreground">Your certificate has been issued and is ready to download from your Certificates page.</p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center mt-2">
            <Button data-testid="course-complete-certificates-btn" onClick={() => nav("/app/certificates")} className="rounded-full bg-coral hover:bg-coral-hover text-white"><Award className="h-4 w-4 mr-2" />View my certificate</Button>
            <Button data-testid="course-complete-close-btn" variant="outline" onClick={() => setCelebrate(false)} className="rounded-full">Close</Button>
          </div>
        </DialogContent>
      </Dialog>
      <button onClick={() => nav(`/app/courses/${id}`)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-brand mb-4"><ArrowLeft className="h-4 w-4" />Back to course</button>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-black rounded-2xl overflow-hidden relative">
            {currentLocked && (
              <div className="w-full aspect-video grid place-items-center text-white/70 flex-col gap-2" data-testid="lesson-locked">
                <Lock className="h-8 w-8" /><span className="text-sm">Pass the previous module to unlock this one.</span>
              </div>
            )}
            {!currentLocked && lesson?.video_url && (
              <VideoPlayer key={lesson.id} lessonKey={lesson.id} src={lesson.video_url} captions={lesson.captions_url} poster={course.thumbnail} onDuration={(d) => setVideoLen((m) => ({ ...m, [lesson.id]: d }))} onProgress={(f) => recordProgress(lesson.id, f)} />
            )}
            {!currentLocked && !lesson?.video_url && <div className="w-full aspect-video grid place-items-center text-white/60">No video</div>}
          </div>

          <div className="bg-cream-card border border-cream-deep rounded-xl p-6">
            <h1 className="text-2xl font-bold text-brand tracking-tight">{lesson?.title}</h1>
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1" data-testid="lesson-length"><Clock className="h-3.5 w-3.5" />{lengthLabel(videoLen[lesson?.id], lesson?.duration_min)}</p>
            {lesson?.content && <p className="text-muted-foreground mt-3 leading-relaxed">{lesson.content}</p>}

            {lesson?.transcript && !currentLocked && (
              <div className="mt-4">
                <button data-testid="transcript-toggle" onClick={() => setShowTranscript((s) => !s)} className="inline-flex items-center gap-2 text-sm font-medium text-brand hover:text-brand-hover">
                  <FileText className="h-4 w-4" />{showTranscript ? "Hide transcript" : "Show transcript"}
                </button>
                {showTranscript && (
                  <div data-testid="transcript-body" className="mt-3 text-sm text-foreground/80 leading-relaxed whitespace-pre-line bg-cream rounded-lg p-4 max-h-80 overflow-auto">{lesson.transcript}</div>
                )}
              </div>
            )}

            {isStaff && !hasQuiz && !currentLocked && (
              <Button data-testid="complete-lesson-btn" onClick={markComplete} disabled={lessonDone}
                className="mt-5 rounded-full bg-coral hover:bg-coral-hover text-white disabled:opacity-60">
                {lessonDone ? <><CheckCircle2 className="h-4 w-4 mr-2" />Completed</> : "Mark module complete"}
              </Button>
            )}
          </div>

          {/* Quiz */}
          {hasQuiz && !currentLocked && quizLocked && (
            <div className="bg-cream-card border border-cream-deep rounded-xl p-6" data-testid="quiz-locked">
              <div className="flex items-center gap-2 mb-1"><Lock className="h-5 w-5 text-brand" /><h2 className="font-heading font-bold text-brand text-lg">Module quiz</h2></div>
              <p className="text-sm text-muted-foreground">Watch at least half of the video to unlock the quiz.</p>
              <div className="mt-3 h-2 rounded-full bg-cream-deep overflow-hidden"><div className="h-full bg-coral transition-[width] duration-500" style={{ width: `${Math.min(100, watchedPct * 100 / QUIZ_UNLOCK_PCT)}%` }} /></div>
              <p className="text-xs text-muted-foreground mt-1" data-testid="quiz-watched-pct">{watchedPct}% watched · unlocks at {QUIZ_UNLOCK_PCT}%</p>
            </div>
          )}
          {hasQuiz && !currentLocked && !quizLocked && (
            <div className="bg-cream-card border border-cream-deep rounded-xl p-6" data-testid="quiz-section">
              <div className="flex items-center gap-2 mb-1"><HelpCircle className="h-5 w-5 text-brand" /><h2 className="font-heading font-bold text-brand text-lg">Module quiz</h2></div>
              <p className="text-sm text-muted-foreground mb-4">Answer all {quiz.length} questions. You need 50% or more to pass.</p>

              {isStaff && lessonDone && !result && (
                <div className="flex items-center gap-2 text-rag-green font-medium" data-testid="quiz-passed-banner"><CheckCircle2 className="h-5 w-5" />You've passed this module's quiz.</div>
              )}

              {showStaffQuiz && (
                <div className="space-y-5">
                  {quiz.map((q, qi) => (
                    <div key={q.id || qi} data-testid={`quiz-q-${qi}`} className={result ? `rounded-xl p-3 -mx-3 ${result.marks?.[qi] ? "bg-rag-green/5" : "bg-rag-red/5"}` : ""}>
                      <p className="font-medium text-foreground mb-2 flex items-start gap-2">
                        {result && <QuizMark ok={result.marks?.[qi]} index={qi} />}
                        <span>{qi + 1}. {q.prompt}</span>
                      </p>
                      <div className="space-y-2">
                        {q.options.map((opt, oi) => {
                          const selected = answers[qi] === oi;
                          return (
                            <button key={`${qi}-${opt}`} data-testid={`quiz-q-${qi}-opt-${oi}`}
                              disabled={!!result && result.passed}
                              onClick={() => setAnswers((a) => ({ ...a, [qi]: oi }))}
                              className={`w-full text-left text-sm px-4 py-2.5 rounded-lg border transition-colors ${selected ? "bg-brand text-white border-brand" : "bg-cream border-cream-deep hover:border-brand"}`}>
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {result && <QuizResultCard result={result} />}

                  {!(result && result.passed) && (
                    result && !result.passed ? (
                      <Button data-testid="retake-quiz-btn" onClick={retake} className="rounded-full bg-coral hover:bg-coral-hover text-white"><RotateCcw className="h-4 w-4 mr-2" />Retake quiz</Button>
                    ) : (
                      <Button data-testid="submit-quiz-btn" onClick={submitQuiz} className="rounded-full bg-coral hover:bg-coral-hover text-white">Submit quiz</Button>
                    )
                  )}
                </div>
              )}
              {!isStaff && (
                <div className="space-y-4">
                  {quiz.map((q, qi) => (
                    <div key={q.id || qi} data-testid={`quiz-preview-${qi}`}>
                      <p className="font-medium text-foreground mb-2">{qi + 1}. {q.prompt}</p>
                      <ul className="space-y-1">
                        {q.options.map((opt, oi) => (
                          <li key={opt} className={`text-sm px-3 py-1.5 rounded-lg ${q.correct_index === oi ? "bg-rag-green/10 text-rag-green font-medium" : "text-muted-foreground"}`}>{opt}{q.correct_index === oi ? " ✓" : ""}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Reflection */}
          {lesson?.reflection_prompt && !currentLocked && (
            <div className="bg-cream-card border border-cream-deep rounded-xl p-6" data-testid="reflection-section">
              <h2 className="font-heading font-bold text-brand text-lg mb-1">Reflection</h2>
              <p className="text-sm text-muted-foreground mb-3">{lesson.reflection_prompt}</p>
              {isStaff ? (
                <>
                  <Textarea data-testid="reflection-input" rows={4} value={reflection} onChange={(e) => setReflection(e.target.value)} placeholder="Share your thoughts…" />
                  <Button data-testid="reflection-save-btn" onClick={saveReflection} className="mt-3 rounded-full bg-brand hover:bg-brand-hover text-white">Save reflection</Button>
                </>
              ) : <p className="text-xs text-muted-foreground italic">Staff will answer this reflective question after the module.</p>}
            </div>
          )}
        </div>

        <div>
          <div className="bg-cream-card border border-cream-deep rounded-xl p-5 sticky top-24">
            <h3 className="font-heading font-bold text-brand">{course.title}</h3>
            {isStaff && enrollment && (
              <div className="mt-3">
                <div className="flex items-center gap-2"><Progress value={enrollment.progress} className="h-2" /><span className="text-xs w-9 text-muted-foreground">{enrollment.progress}%</span></div>
                {enrollment.status === "completed" && <div className="flex items-center gap-2 mt-3 text-rag-green text-sm font-medium"><Award className="h-4 w-4" />Certificate earned</div>}
              </div>
            )}
            <div className="mt-4 space-y-1">
              {lessons.map((l, i) => {
                const locked = isLocked(i);
                return (
                  <button key={l.id} data-testid={`lesson-item-${i}`} disabled={locked} onClick={() => !locked && setActive(i)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${lessonClass(i === active, locked)}`}>
                    <LessonIcon done={completed.has(l.id)} locked={locked} current={i === active} />
                    <span className="flex-1">{l.title}</span>
                    {!!(l.quiz || []).length && <span className="text-[10px] uppercase tracking-wide text-muted-foreground">quiz</span>}
                    <span className="text-xs text-muted-foreground">{l.duration_min}m</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
